import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Badge,
  ProgressBar,
  ButtonGroup,
  Button,
} from 'react-bootstrap';
import Chart from 'react-apexcharts';
import type {
  FormAnalytics,
  FeedbackAnalyticsDto,
  QuestionAnalytics,
} from '../../../../services/feedback-analytics';
import InfoHint from './InfoHint';

interface FormAnalyticsChartProps {
  data: FeedbackAnalyticsDto | null;
  loading?: boolean;
}

export const FormAnalyticsChart: React.FC<FormAnalyticsChartProps> = ({
  data,
  loading = false,
}) => {
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);

  if (!data || data.allForms.length === 0) {
    return (
      <div className="alert alert-info text-center my-4">
        Nenhum formulário com dados
      </div>
    );
  }

  const selectedForm = selectedFormId
    ? data.allForms.find((f: FormAnalytics) => f.formId === selectedFormId)
    : data.allForms[0];

  if (!selectedForm) {
    return null;
  }

  const handleFormSelect = (formId: number) => {
    setSelectedFormId(formId);
  };

  return (
    <Card className="shadow-sm border-0 mb-5">
      <Card.Header className="bg-white border-bottom">
        <Card.Title className="mb-3 d-flex align-items-center">
          Análise por Formulário
          <InfoHint
            id="form-analysis"
            text="Compara desempenho de cada formulário considerando média, NPS, volume de respostas e anonimato."
          />
        </Card.Title>
        <ButtonGroup className="d-flex flex-wrap gap-2">
          {data.allForms.map((form: FormAnalytics) => (
            <Button
              key={form.formId}
              variant={selectedForm?.formId === form.formId ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => handleFormSelect(form.formId)}
              className="text-truncate"
              style={{ maxWidth: '200px' }}
            >
              {form.formTitle}
              <Badge bg={form.averageRating >= 7 ? 'success' : form.averageRating >= 5 ? 'warning' : 'danger'} className="ms-2">
                {form.averageRating.toFixed(1)}
              </Badge>
              <Badge
                bg={form.npsSummary.npsScore >= 0 ? 'success' : 'danger'}
                className="ms-1"
              >
                NPS {form.npsSummary.npsScore}
              </Badge>
            </Button>
          ))}
        </ButtonGroup>
      </Card.Header>

      <Card.Body>
        <FormDetailTab form={selectedForm} />
      </Card.Body>
    </Card>
  );
};

interface FormDetailTabProps {
  form: FormAnalytics;
}

const FormDetailTab: React.FC<FormDetailTabProps> = ({ form }) => {
  const distributionChartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'donut',
    },
    labels: form.ratingDistribution
      .filter((r: any) => r.count > 0)
      .map((r: any) => `${r.score} ⭐`),
    colors: [
      '#ffc107',
      '#ffc107',
      '#ffc107',
      '#28a745',
      '#28a745',
      '#28a745',
      '#0d6efd',
      '#0d6efd',
      '#0d6efd',
      '#dc3545',
      '#dc3545',
    ],
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: {
              offsetY: 5,
            },
            value: {
              offsetY: -5,
            },
          },
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} avaliações`,
      },
    },
  };

  const distributionChartSeries = form.ratingDistribution
    .filter((r: any) => r.count > 0)
    .map((r: any) => r.count);

  return (
    <div>
      {/* Resumo */}
      <Row className="mb-5 g-3">
        <Col xs={12} sm={6} md={3}>
          <div className="bg-light p-3 rounded">
            <small className="text-muted d-block mb-2">Total de Avaliações</small>
            <h5 style={{ color: '#0d6efd' }}>
              {form.totalReviews}
            </h5>
          </div>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <div className="bg-light p-3 rounded">
            <small className="text-muted d-block mb-2">Avaliação Média</small>
            <div className="d-flex align-items-center gap-2">
              <h5 style={{ color: '#ffc107', marginBottom: 0 }}>
                {form.averageRating.toFixed(1)}
              </h5>
              <span style={{ fontSize: '20px' }}>⭐</span>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <div className="bg-light p-3 rounded">
            <small className="text-muted d-block mb-2">NPS do Formulário</small>
            <h5
              style={{
                color: form.npsSummary.npsScore >= 0 ? '#198754' : '#dc3545',
              }}
            >
              {form.npsSummary.npsScore}
            </h5>
            <small className="text-muted d-block">
              P {form.npsSummary.promotersPercentage}% | N {form.npsSummary.passivesPercentage}% | D {form.npsSummary.detractorsPercentage}%
            </small>
          </div>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <div className="bg-light p-3 rounded">
            <small className="text-muted d-block mb-2">Identificadas</small>
            <h5 style={{ color: '#28a745' }}>
              {form.namedCount}
            </h5>
          </div>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <div className="bg-light p-3 rounded">
            <small className="text-muted d-block mb-2">Anônimas</small>
            <h5 style={{ color: '#6c757d' }}>
              {form.anonymousCount}
            </h5>
          </div>
        </Col>
      </Row>

      {/* Gráfico de Distribuição */}
      {distributionChartSeries.length > 0 && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <Chart
              options={distributionChartOptions}
              series={distributionChartSeries}
              type="donut"
              height={300}
            />
          </Card.Body>
        </Card>
      )}

      {/* Perguntas */}
      {form.questions.length > 0 && (
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <Card.Title className="mb-0 d-flex align-items-center">
              Análise das Perguntas
              <InfoHint
                id="questions-analysis"
                text="Detalha métricas por pergunta, incluindo média, distribuição e respostas mais frequentes."
              />
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {form.questions.map((question: QuestionAnalytics, questionIndex: number) => (
                <div
                  key={`${question.order}-${question.type}-${question.label}-${questionIndex}`}
                  className="border-start border-info ps-3 py-2"
                  style={{ borderLeftWidth: '4px' }}
                >
                  <div className="fw-semibold text-dark mb-1">
                    {question.order + 1}. {question.label}
                  </div>
                  <div className="text-sm text-muted mb-2">
                    Tipo: <Badge bg="secondary">{question.type}</Badge> | Respostas:{' '}
                    <strong>{question.metrics.totalResponses}</strong>
                  </div>

                  {question.metrics.averageScore !== undefined && (
                    <div className="mb-2">
                      <div className="small mb-1">
                        Média: {question.metrics.averageScore.toFixed(1)}/10
                      </div>
                      <ProgressBar
                        now={Math.round(
                          (question.metrics.averageScore / 10) * 100,
                        )}
                        variant={
                          question.metrics.averageScore >= 7
                            ? 'success'
                            : question.metrics.averageScore >= 5
                              ? 'warning'
                              : 'danger'
                        }
                        style={{ height: '20px' }}
                      />
                    </div>
                  )}

                  {question.metrics.topResponses &&
                    question.metrics.topResponses.length > 0 && (
                      <div className="small">
                        <div className="text-muted mb-1">Principais respostas:</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {question.metrics.topResponses?.slice(0, 3).map((resp: string) => (
                            <div
                              key={resp}
                              className="text-dark bg-light px-2 py-1 rounded small"
                            >
                              • {resp}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default FormAnalyticsChart;
